
import React from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

interface WorldviewInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  worldview: string | null;
  onStartConversation: () => void;
}

const WorldviewInstructionsDialog: React.FC<WorldviewInstructionsDialogProps> = ({
  open,
  onOpenChange,
  worldview,
  onStartConversation,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-[95%] mx-auto sm:w-full sm:max-w-2xl bg-white p-4 sm:p-6"
      >
        <DialogHeader className="space-y-1 sm:space-y-2">
          <DialogTitle className="flex items-center gap-2 text-heaven-dark-gold text-lg sm:text-xl">
            <AlertCircle className="h-5 w-5" />
            Live Gospel Conversation
          </DialogTitle>
          <DialogDescription className="text-sm">
            You've chosen to speak with someone from the {worldview} worldview.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-2 space-y-2 sm:space-y-3">
          <p className="text-xs sm:text-sm text-gray-800">
            You are about to begin a live gospel conversation with someone from another faith or worldview.
          </p>
          <p className="text-xs sm:text-sm text-gray-800">
            They believe what they believe—and they believe you're wrong.
          </p>
          
          <h3 className="font-medium text-sm sm:text-base text-gray-900">Your job is to:</h3>
          <ul className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm"> 
            <li className="flex items-start">
              <span className="font-medium mr-2">•</span>
              <span>Speak clearly and kindly</span>
            </li>
            <li className="flex items-start">
              <span className="font-medium mr-2">•</span>
              <span>Use the Bible boldly</span>
            </li>
            <li className="flex items-start">
              <span className="font-medium mr-2">•</span>
              <span>Ask thoughtful questions</span>
            </li>
            <li className="flex items-start">
              <span className="font-medium mr-2">•</span>
              <span>Keep Jesus at the center</span>
            </li>
            <li className="flex items-start">
              <span className="font-medium mr-2">•</span>
              <span>Stay patient and courageous</span>
            </li>
          </ul>
          
          <p className="text-xs sm:text-sm text-gray-800">
            They will only begin to soften once you show consistency in love and truth.
            Speak like you would in real life.
            <br />
            Be bold. Be humble. Be rooted in Scripture.
            <br />
            And pray as you type.
          </p>
          
          <div className="bg-amber-50 p-1.5 sm:p-2 rounded-md border border-amber-200"> 
            <p className="text-xs sm:text-sm font-medium text-amber-800">
              Remember: You're not debating a chatbot.
              <br />
              You're training for real spiritual warfare.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            onClick={onStartConversation}
            className="w-full bg-heaven-gold hover:bg-heaven-dark-gold text-white text-sm"
          >
            Now go. Lead them to Jesus.
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorldviewInstructionsDialog;
